import { createContext, useContext, useEffect, useMemo, useState } from 'react';
// @ts-ignore
import { IntlProvider } from 'react-intl';
import DirectionProvider, { DIRECTIONS } from 'react-with-direction/dist/DirectionProvider';

import { useApp } from '@lib/core/service/hooks';
import { LocaleUtils } from '@lib/tools/locale/utils';

import '@formatjs/intl-pluralrules/polyfill.js';
import '@formatjs/intl-pluralrules/locale-data/ar.js';
import '@formatjs/intl-pluralrules/locale-data/en.js';
import '@formatjs/intl-pluralrules/locale-data/bg.js';
import '@formatjs/intl-pluralrules/locale-data/bs.js';
import '@formatjs/intl-pluralrules/locale-data/cs.js';
import '@formatjs/intl-pluralrules/locale-data/da.js';
import '@formatjs/intl-pluralrules/locale-data/de.js';
import '@formatjs/intl-pluralrules/locale-data/el.js';
import '@formatjs/intl-pluralrules/locale-data/es.js';
import '@formatjs/intl-pluralrules/locale-data/fr.js';
import '@formatjs/intl-pluralrules/locale-data/he.js';
import '@formatjs/intl-pluralrules/locale-data/hr.js';
import '@formatjs/intl-pluralrules/locale-data/hu.js';
import '@formatjs/intl-pluralrules/locale-data/id.js';
import '@formatjs/intl-pluralrules/locale-data/it.js';
import '@formatjs/intl-pluralrules/locale-data/ja.js';
import '@formatjs/intl-pluralrules/locale-data/kk.js';
import '@formatjs/intl-pluralrules/locale-data/ko.js';
import '@formatjs/intl-pluralrules/locale-data/mk.js';
import '@formatjs/intl-pluralrules/locale-data/ms.js';
import '@formatjs/intl-pluralrules/locale-data/pl.js';
import '@formatjs/intl-pluralrules/locale-data/pt.js';
import '@formatjs/intl-pluralrules/locale-data/ro.js';
import '@formatjs/intl-pluralrules/locale-data/ru.js';
import '@formatjs/intl-pluralrules/locale-data/sk.js';
import '@formatjs/intl-pluralrules/locale-data/sl.js';
import '@formatjs/intl-pluralrules/locale-data/sr.js';
import '@formatjs/intl-pluralrules/locale-data/tr.js';

/**
 * Context to expose locale loading state
 */
interface LocaleContextType {
  isLocaleReady: boolean;
}

const LocaleContext = createContext<LocaleContextType>({ isLocaleReady: false });

/**
 * Hook to check if locale messages are loaded
 * @returns {boolean} true when locale messages are ready
 */
export const useLocaleReady = (): boolean => {
  const { isLocaleReady } = useContext(LocaleContext);
  return isLocaleReady;
};

/**
 * * Locale Provider (HOC)
 *
 * This provider is responsible for translations, locale configuration, and text direction.
 */
export const LocaleProvider = ({ children }) => {
  const { locale, isLayoutRightToLeft, isLocaleEditMode } = useApp();

  const [messages, setMessages] = useState({});
  const [isLocaleReady, setIsLocaleReady] = useState(false);

  /**
   * * Fetch translations from S3 for default mode
   * * Fetch translations from backend for edit mode
   */
  useEffect(() => {
    let isMounted = true;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let draftTermsLoaded = false;

    const loadMessages = async () => {
      setIsLocaleReady(false);
      draftTermsLoaded = false;

      try {
        if (isLocaleEditMode) {
          // getDraftTerms uses a callback, so we need to handle it differently
          LocaleUtils.getDraftTerms(locale, newMessages => {
            if (isMounted && !draftTermsLoaded) {
              draftTermsLoaded = true;
              // Clear the timeout if it's still pending
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              setMessages(newMessages);
              setIsLocaleReady(true);
            }
          });
          // If callback is not called (e.g., draft terms not available),
          // we need a fallback. Since getDraftTerms doesn't return a promise,
          // we'll set a timeout to ensure we don't block forever.
          timeoutId = setTimeout(() => {
            if (isMounted && !draftTermsLoaded) {
              draftTermsLoaded = true;
              // Fallback to published terms if draft terms don't load
              LocaleUtils.getPublishedTerms(locale)
                .then(publishedTerms => {
                  if (isMounted) {
                    setMessages(publishedTerms);
                    setIsLocaleReady(true);
                  }
                })
                .catch(error => {
                  console.error('Error loading fallback published terms:', error);
                  if (isMounted) {
                    setIsLocaleReady(true); // Set to true even on error to avoid blocking the UI
                  }
                });
            }
          }, 1000);
        } else {
          const publishedTerms = await LocaleUtils.getPublishedTerms(locale);
          if (isMounted) {
            setMessages(publishedTerms);
            setIsLocaleReady(true);
          }
        }
      } catch (error) {
        console.error('Error loading locale messages:', error);
        if (isMounted) {
          setIsLocaleReady(true); // Set to true even on error to avoid blocking the UI
        }
      }

      // * Direction change
      if (isMounted) {
        document.getElementsByTagName('body')[0].style.textAlign = isLayoutRightToLeft ? 'right' : 'left';
      }
    };

    loadMessages();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [locale, isLocaleEditMode, isLayoutRightToLeft]);

  const contextValue = useMemo(() => ({ isLocaleReady }), [isLocaleReady]);

  return (
    <LocaleContext.Provider value={contextValue}>
      <IntlProvider
        locale={locale}
        messages={messages}
        onError={e => {
          if (e.code === 'MISSING_TRANSLATION') {
            // console.warn('Missing locale', e.descriptor);
            return;
          }
          console.error(e);
        }}
      >
        <DirectionProvider direction={isLayoutRightToLeft ? DIRECTIONS.RTL : DIRECTIONS.LTR}>
          <div lang={locale}>{children}</div>
        </DirectionProvider>
      </IntlProvider>
    </LocaleContext.Provider>
  );
};
